import React, { useEffect, useState } from 'react';
import {
    Avatar,
    Box,
    Button,
    Card,
    CardContent,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    TextField,
    Typography,
} from '@mui/material';
import { appUserApi, fetcher } from 'lib/api';
import styles from './SendUserNotificationModal.module.scss';

const emptyForm = {
    title: '',
    body: '',
    link: '',
    image: '',
};

export default function SendUserNotificationModal({ open, onClose, onSent, userId, t }) {
    const [form, setForm] = useState(emptyForm);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        if (open) {
            setForm(emptyForm);
            setError('');
            setSubmitting(false);
        }
    }, [open]);

    const change = (field, value) => {
        setForm((prev) => ({ ...prev, [field]: value }));
        setError('');
    };

    const canSend = form.title.trim() && form.body.trim() && !submitting;

    const handleSend = async () => {
        if (!canSend || !userId) return;

        setSubmitting(true);
        setError('');
        try {
            const payload = {
                title: form.title.trim(),
                body: form.body.trim(),
            };
            if (form.link.trim()) payload.link = form.link.trim();
            if (form.image.trim()) payload.image = form.image.trim();

            const res = await fetcher(appUserApi.notificationSend(userId), 'POST', payload);
            if (res?.error) {
                setError(res.message || t('user_notification.send_failed'));
                return;
            }
            onSent?.();
            onClose?.();
        } catch (e) {
            const apiMessage =
                e?.response?.data?.message || e?.message || t('user_notification.send_failed');
            setError(apiMessage);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Dialog
            open={open}
            onClose={submitting ? undefined : onClose}
            maxWidth={false}
            PaperProps={{
                sx: {
                    width: { xs: '95vw', sm: 520 },
                    maxWidth: { xs: '95vw', sm: 520 },
                    borderRadius: 3,
                },
            }}
        >
            <DialogTitle className={styles.dialogTitle}>
                {t('send_notification')}
            </DialogTitle>
            <DialogContent className={styles.dialogContent}>
                <TextField
                    className={styles.field}
                    label={t('user_notification.field_title')}
                    value={form.title}
                    onChange={(e) => change('title', e.target.value)}
                    fullWidth
                    required
                    inputProps={{ maxLength: 255 }}
                />
                <TextField
                    className={styles.field}
                    label={t('user_notification.field_body')}
                    value={form.body}
                    onChange={(e) => change('body', e.target.value)}
                    fullWidth
                    required
                    multiline
                    minRows={3}
                    inputProps={{ maxLength: 1000 }}
                />
                <TextField
                    className={styles.field}
                    label={t('user_notification.field_link')}
                    value={form.link}
                    onChange={(e) => change('link', e.target.value)}
                    fullWidth
                    placeholder="https://www.spara.is/..."
                />
                <TextField
                    className={styles.field}
                    label={t('user_notification.field_image')}
                    value={form.image}
                    onChange={(e) => change('image', e.target.value)}
                    fullWidth
                    placeholder="https://..."
                />

                <Typography className={styles.previewLabel}>
                    {t('notification_preview')}
                </Typography>
                <Card className={styles.previewCard}>
                    <CardContent className={styles.previewContent}>
                        <Box display="flex" alignItems="flex-start" gap={2}>
                            <Avatar
                                src={form.image.trim() || '/spara-app-icon.png'}
                                alt=""
                                sx={{ width: 44, height: 44 }}
                            />
                            <Box flex={1} minWidth={0}>
                                <Typography className={styles.previewTitle}>
                                    {form.title.trim() || t('user_notification.field_title')}
                                </Typography>
                                <Typography className={styles.previewBody}>
                                    {form.body.trim() || t('user_notification.field_body')}
                                </Typography>
                            </Box>
                        </Box>
                    </CardContent>
                </Card>

                {error ? (
                    <Typography color="error" className={styles.error}>
                        {error}
                    </Typography>
                ) : null}
            </DialogContent>
            <DialogActions className={styles.actions}>
                <Button onClick={onClose} disabled={submitting}>
                    {t('cancel')}
                </Button>
                <Button
                    variant="contained"
                    onClick={handleSend}
                    disabled={!canSend}
                    sx={{
                        backgroundColor: '#DE2C48',
                        '&:hover': { backgroundColor: '#b52038' },
                    }}
                >
                    {submitting ? t('user_notification.sending') : t('send')}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
